import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Gnavbar from "../../components/Gnavbar";
import BottomNavbar from "../../components/BottomNavbar";
import { RootState } from "../../store";
import ProfileViaForm from "./components/ProfileViaForm";
import { useLanguage } from "../../context/LanguageContext";
import Navbar from "../MyWebSite/Components/Navbar";
import { getRequest } from "../../helpers/api.helper";
import { toast } from "react-toastify";
import { shareViaNative } from "../../helpers/nativeShare";
import { Share2 } from "lucide-react";
import ProfileCompletionSuccess from "./ProfileCompletionSuccess";

type BasicParams = {
  whose?: "own" | "mediator";
  bureauId?: string;
};

type PublicBureauMeta = {
  bureauName?: string;
  navbarLogo?: string;
};

const AddProfileViaBasic: React.FC = () => {
  const navigate = useNavigate();
  const { whose, bureauId: routeBureauId } = useParams<BasicParams>();
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const { t } = useLanguage();
  const [publicBureauMeta, setPublicBureauMeta] = useState<PublicBureauMeta>(
    {},
  );
  const [isLoadingBureau, setIsLoadingBureau] = useState(false);
  const [createdProfile, setCreatedProfile] = useState<any>(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const isPublicRoute = Boolean(routeBureauId);
  const isMediatorProfile = whose === "mediator";
  const hidePublicNavbar = isPublicRoute && isMediatorProfile;
  const bureauId = bureauInfo?.bureauId || routeBureauId || "";

  const publicBasicProfileUrl = useMemo(() => {
    if (!bureauInfo?.bureauId || !isMediatorProfile) return "";
    return `https://matrimonystudio.in/profile/${bureauInfo.bureauId}/basic/mediator`;
  }, [bureauInfo?.bureauId, isMediatorProfile]);

  useEffect(() => {
    if (!isPublicRoute || !routeBureauId) return;

    const fetchPublicBureau = async () => {
      setIsLoadingBureau(true);
      try {
        const response = await getRequest(`/bureau/website/${routeBureauId}`);
        if (response.success) {
          setPublicBureauMeta({
            bureauName: response.data?.profile?.bureauName || "",
            navbarLogo: response.data?.profile?.navbarLogo || "",
          });
          return;
        }

        toast.error(response.message || "Invalid bureau link");
      } catch (error) {
        console.error("Failed to load bureau info", error);
        toast.error("Unable to load bureau details");
      } finally {
        setIsLoadingBureau(false);
      }
    };

    fetchPublicBureau();
  }, [isPublicRoute, routeBureauId]);

  const handleCopyPublicUrl = async () => {
    if (!publicBasicProfileUrl) return;

    try {
      await navigator.clipboard.writeText(publicBasicProfileUrl);
      toast.success("Basic details URL copied");
    } catch (error) {
      console.error("Failed to copy public URL", error);
      toast.error("Failed to copy URL");
    }
  };

  const handleSharePublicUrl = async () => {
    if (!publicBasicProfileUrl) return;

    try {
      await shareViaNative({
        title: "Mediator Basic Profile Form",
        text: "Open this link and submit mediator basic details.",
        url: publicBasicProfileUrl,
      });
    } catch (error) {
      console.error("Failed to share public URL", error);
      await handleCopyPublicUrl();
      toast.info("Sharing not supported. URL copied instead.");
    }
  };

  const handleBasicProfileSuccess = async (userData?: any) => {
    const fallbackProfile = userData ?? {};
    const martialId = fallbackProfile?.martialId || fallbackProfile?.maritalId;

    if (!martialId) {
      setCreatedProfile(fallbackProfile);
      return;
    }

    try {
      const response = await getRequest(`/bureau/profile/${martialId}`);
      if (response?.data) {
        setCreatedProfile({
          ...response.data,
          martialId: response.data.martialId || martialId,
          password: fallbackProfile?.password ?? response.data?.password,
        });
        return;
      }
    } catch (error) {
      console.error("Failed to load created profile for sharing", error);
    }

    setCreatedProfile(fallbackProfile);
  };

  if (!bureauId) {
    return (
      <div className="min-h-screen bg-gray-50">
        {isPublicRoute ? (
          !hidePublicNavbar ? (
            <Navbar
              navbarLogo={publicBureauMeta.navbarLogo}
              bureauName={publicBureauMeta.bureauName}
            />
          ) : null
        ) : (
          <div className="hidden md:block">
            <Gnavbar />
          </div>
        )}
        <div className="max-w-4xl mx-auto px-4 py-10">
          <p className="text-gray-700">
            {t("Invalid bureau session. Please re-login.")}
          </p>
        </div>
      </div>
    );
  }

  if (isPublicRoute && isLoadingBureau) {
    return (
      <div className="min-h-screen bg-white">
        {!hidePublicNavbar ? (
          <Navbar
            navbarLogo={publicBureauMeta.navbarLogo}
            bureauName={publicBureauMeta.bureauName}
          />
        ) : null}
        <div
          className={`mx-auto w-full max-w-7xl px-4 md:px-6 ${
            hidePublicNavbar ? "pt-6" : "pt-32"
          } pb-20`}
        >
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-gray-600">
            {t("Loading")}...
          </div>
        </div>
      </div>
    );
  }

  // const pageTitle = `${t("Add Basic Details")} - ${
  //   isMediatorProfile ? t("Mediator") : t("Own")
  // }`;

  const pageTitle = "Create Marriage Biodata";
  const pageSubtitle = "Enter basic details with voice message";
  const pageHighlights = [
    "Download unlimited profiles for free",
    "Get 10x better response for your profile",
  ];
  const pageTopSpacing = isPublicRoute && !hidePublicNavbar ? "pt-32" : "pt-6";

  if (createdProfile) {
    return (
      <ProfileCompletionSuccess
        userData={createdProfile}
        whatsAppShareMode="options"
        onGoToDashboard={() =>
          isPublicRoute ? navigate(`/profile/${bureauId}`) : navigate(-1)
        }
        onClose={() => {
          setCreatedProfile(null);
          setFormResetKey((prev) => prev + 1);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {isPublicRoute ? (
        !hidePublicNavbar ? (
          <Navbar
            navbarLogo={publicBureauMeta.navbarLogo}
            bureauName={publicBureauMeta.bureauName}
          />
        ) : null
      ) : (
        <div className="hidden md:block">
          <Gnavbar />
        </div>
      )}
      <div
        className={`mx-auto w-full max-w-7xl px-0 md:px-6 ${pageTopSpacing} pb-28 sm:pb-20`}
      >
        <div className="px-4 pb-4 md:px-2">
          <div className="mb-3 flex items-start gap-3">
            <button
              type="button"
              onClick={() =>
                isPublicRoute ? navigate(`/profile/${bureauId}`) : navigate(-1)
              }
              className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-blue-600 transition hover:bg-gray-200"
            >
              <span className="sr-only">Back</span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            {isMediatorProfile && (
              <div className="min-w-0">
                <span className="text-gray-600 text-xs block">
                  Get 10x better response for your profile
                </span>
                <h1 className="text-xl font-bold text-gray-900 md:text-2xl">
                  {pageTitle}
                </h1>
                <p className="text-base text-gray-600 md:text-lg">
                  {pageSubtitle}
                </p>
              </div>
            )}
          </div>
          {isMediatorProfile && (
            <span className="hidden text-gray-600 text-xs md:block">
              Get 10x better response for your profile
            </span>
          )}
        </div>

        <ProfileViaForm
          key={formResetKey}
          bureauId={bureauId}
          isMediatorProfile={isMediatorProfile}
          onBack={() =>
            isPublicRoute ? navigate(`/profile/${bureauId}`) : navigate(-1)
          }
          onSuccess={handleBasicProfileSuccess}
          variant="page"
          headerActions={
            !isPublicRoute && isMediatorProfile && publicBasicProfileUrl ? (
              <button
                type="button"
                onClick={handleSharePublicUrl}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-purple-700"
                title={publicBasicProfileUrl}
              >
                <Share2 size={16} />
                Share
              </button>
            ) : undefined
          }
        />
      </div>
      {!isPublicRoute ? <BottomNavbar /> : null}
    </div>
  );
};

export default AddProfileViaBasic;
